<template>
  <div class="student-report-container">
    <!-- 报告头部 -->
    <div class="report-header">
      <h3><i class="el-icon-document"></i> 学习报告</h3>
      <div class="report-actions">
        <el-button type="primary" size="small" @click="generateReport" :loading="generating">
          <i class="el-icon-refresh"></i> 生成报告
        </el-button>
        <el-button size="small" @click="refreshReport" :loading="loading">
          <i class="el-icon-refresh"></i> 刷新数据
        </el-button>
        <el-button size="small" @click="exportReport">
          <i class="el-icon-download"></i> 导出报告
        </el-button>
      </div>
    </div>

    <!-- 报告内容 -->
    <div v-if="reportData" class="report-content">
      <!-- 学习概览 -->
      <el-card class="overview-card">
        <div slot="header">
          <span>学习概览</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="overview-item">
              <div class="overview-icon">
                <i class="el-icon-time"></i>
              </div>
              <div class="overview-content">
                <div class="overview-number">{{ reportData.totalStudyTime }}</div>
                <div class="overview-label">总学习时长（分钟）</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="overview-item">
              <div class="overview-icon">
                <i class="el-icon-date"></i>
              </div>
              <div class="overview-content">
                <div class="overview-number">{{ reportData.studyDays }}</div>
                <div class="overview-label">学习天数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="overview-item">
              <div class="overview-icon">
                <i class="el-icon-timer"></i>
              </div>
              <div class="overview-content">
                <div class="overview-number">{{ reportData.averageStudyTimePerDay ? reportData.averageStudyTimePerDay.toFixed(1) : '0.0' }}</div>
                <div class="overview-label">日均学习时长（分钟）</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="overview-item">
              <div class="overview-icon">
                <i class="el-icon-trophy"></i>
              </div>
              <div class="overview-content">
                <div class="overview-number">{{ reportData.homeworkProgress && reportData.homeworkProgress.averageScore ? reportData.homeworkProgress.averageScore.toFixed(1) : '0.0' }}</div>
                <div class="overview-label">作业平均分</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 作业和考试情况 -->
      <el-row :gutter="20" class="progress-row">
        <el-col :span="12">
          <el-card class="progress-card">
            <div slot="header">
              <span>作业完成情况</span>
            </div>
            <div class="progress-content">
              <div class="progress-item">
                <div class="progress-label">完成率</div>
                <el-progress
                  :percentage="Math.round((reportData.homeworkProgress && reportData.homeworkProgress.completionRate) || 0)"
                  :color="getProgressColor((reportData.homeworkProgress && reportData.homeworkProgress.completionRate) || 0)"
                />
                <div class="progress-text">
                  {{ (reportData.homeworkProgress && reportData.homeworkProgress.completedHomework) || 0 }}/{{ (reportData.homeworkProgress && reportData.homeworkProgress.totalHomework) || 0 }} 个作业
                </div>
              </div>
              <div class="progress-item">
                <div class="progress-label">按时完成率</div>
                <el-progress
                  :percentage="Math.round(((reportData.homeworkProgress && reportData.homeworkProgress.onTimeHomework) || 0) / ((reportData.homeworkProgress && reportData.homeworkProgress.totalHomework) || 1) * 100)"
                  :color="getProgressColor(((reportData.homeworkProgress && reportData.homeworkProgress.onTimeHomework) || 0) / ((reportData.homeworkProgress && reportData.homeworkProgress.totalHomework) || 1) * 100)"
                />
                <div class="progress-text">
                  {{ (reportData.homeworkProgress && reportData.homeworkProgress.onTimeHomework) || 0 }}/{{ (reportData.homeworkProgress && reportData.homeworkProgress.totalHomework) || 0 }} 个按时完成
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="progress-card">
            <div slot="header">
              <span>考试情况</span>
            </div>
            <div class="progress-content">
              <div class="progress-item">
                <div class="progress-label">参与率</div>
                <el-progress
                  :percentage="Math.round((reportData.examProgress && reportData.examProgress.participationRate) || 0)"
                  :color="getProgressColor((reportData.examProgress && reportData.examProgress.participationRate) || 0)"
                />
                <div class="progress-text">
                  {{ (reportData.examProgress && reportData.examProgress.participatedExams) || 0 }}/{{ (reportData.examProgress && reportData.examProgress.totalExams) || 0 }} 次考试
                </div>
              </div>
              <div class="progress-item">
                <div class="progress-label">平均分</div>
                <div class="score-display">
                  <span class="score-number">{{ (reportData.examProgress && reportData.examProgress.averageScore) ? reportData.examProgress.averageScore.toFixed(1) : '0.0' }}</span>
                  <span class="score-range">
                    (最高: {{ (reportData.examProgress && reportData.examProgress.highestScore) ? reportData.examProgress.highestScore.toFixed(1) : '0.0' }},
                    最低: {{ (reportData.examProgress && reportData.examProgress.lowestScore) ? reportData.examProgress.lowestScore.toFixed(1) : '0.0' }})
                  </span>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 知识点掌握情况 -->
      <el-card class="knowledge-card">
        <div slot="header">
          <span>知识点掌握情况</span>
        </div>
        <div class="knowledge-content">
          <div v-for="knowledge in reportData.knowledgeMastery" :key="knowledge.knowledgeName" class="knowledge-item">
            <div class="knowledge-header">
              <span class="knowledge-name">{{ knowledge.knowledgeName }}</span>
              <el-tag :type="getMasteryStatusType(knowledge.masteryStatus)" size="small">
                {{ knowledge.masteryStatus }}
              </el-tag>
            </div>
            <div class="knowledge-progress">
              <el-progress
                :percentage="Math.round(knowledge.masteryLevel)"
                :color="getMasteryColor(knowledge.masteryLevel)"
                :show-text="false"
              />
              <div class="knowledge-stats">
                <span>掌握程度: {{ knowledge.masteryLevel ? knowledge.masteryLevel.toFixed(1) : '0.0' }}%</span>
                <span>练习数: {{ knowledge.practiceCount }}</span>
                <span>正确率: {{ knowledge.correctRate ? knowledge.correctRate.toFixed(1) : '0.0' }}%</span>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 学习轨迹 -->
      <el-card class="track-card">
        <div slot="header">
          <span>学习轨迹</span>
        </div>
        <div class="track-content">
          <div v-for="track in reportData.studyTracks" :key="track.date" class="track-item">
            <div class="track-date">{{ track.date }}</div>
            <div class="track-info">
              <div class="track-content-text">{{ track.studyContent }}</div>
              <div class="track-meta">
                <el-tag size="mini" :type="getStudyTypeType(track.studyType)">{{ track.studyType }}</el-tag>
                <span class="track-time">{{ track.studyTime }} 分钟</span>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 学习建议和成就 -->
      <el-row :gutter="20" class="suggestions-row">
        <el-col :span="12">
          <el-card class="suggestions-card">
            <div slot="header">
              <span>学习建议</span>
            </div>
            <ul class="suggestions-list">
              <li v-for="(suggestion, index) in reportData.suggestions" :key="index">
                <i class="el-icon-lightbulb"></i>
                {{ suggestion }}
              </li>
            </ul>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="achievements-card">
            <div slot="header">
              <span>学习成就</span>
            </div>
            <ul class="achievements-list">
              <li v-for="(achievement, index) in reportData.achievements" :key="index">
                <i class="el-icon-trophy"></i>
                {{ achievement }}
              </li>
            </ul>
          </el-card>
        </el-col>
      </el-row>

      <!-- 需要改进的方面 -->
      <el-card class="improvements-card">
        <div slot="header">
          <span>需要改进的方面</span>
        </div>
        <div class="improvements-content">
          <el-tag
            v-for="(improvement, index) in reportData.improvements"
            :key="index"
            type="warning"
            size="medium"
            style="margin-right: 10px; margin-bottom: 10px;"
          >
            {{ improvement }}
          </el-tag>
        </div>
      </el-card>
    </div>

    <!-- 无数据状态 -->
    <el-card v-else class="empty-card">
      <div class="empty-content">
        <i class="el-icon-document empty-icon"></i>
        <h3>暂无学习报告数据</h3>
        <p>点击下方按钮生成您的学习报告</p>
        <el-button type="primary" @click="generateReport" :loading="generating">
          <i class="el-icon-refresh"></i> 生成学习报告
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getStudentReport, generateStudentReport } from '@/api/studentReport'

export default {
  name: 'StudentReport',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      reportData: null,
      loading: false,
      generating: false
    }
  },
  mounted() {
    this.loadReport()
  },
  methods: {
    // 加载学习报告数据
    async loadReport() {
      this.loading = true
      try {
        const response = await getStudentReport(this.courseId)
        if (response.code === 1) {
          this.reportData = response.data
        } else {
          this.$message.error(response.msg || '获取学习报告失败')
        }
      } catch (error) {
        console.error('获取学习报告失败:', error)
        this.$message.error('获取学习报告失败')
      } finally {
        this.loading = false
      }
    },

    // 生成学习报告
    async generateReport() {
      this.generating = true
      try {
        const response = await generateStudentReport(this.courseId)
        if (response.code === 1) {
          this.reportData = response.data
          this.$message.success('学习报告生成成功')
        } else {
          this.$message.error(response.msg || '生成学习报告失败')
        }
      } catch (error) {
        console.error('生成学习报告失败:', error)
        this.$message.error('生成学习报告失败')
      } finally {
        this.generating = false
      }
    },

    // 刷新报告数据
    refreshReport() {
      this.loadReport()
    },

    // 导出报告
    exportReport() {
      this.$message.info('导出功能开发中...')
    },

    // 获取进度条颜色
    getProgressColor(percentage) {
      if (percentage >= 90) return '#67C23A'
      if (percentage >= 70) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取掌握程度颜色
    getMasteryColor(level) {
      if (level >= 80) return '#67C23A'
      if (level >= 60) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取掌握状态类型
    getMasteryStatusType(status) {
      const statusMap = {
        '优秀': 'success',
        '良好': 'success',
        '一般': 'warning',
        '需加强': 'danger'
      }
      return statusMap[status] || 'info'
    },

    // 获取学习类型类型
    getStudyTypeType(type) {
      const typeMap = {
        '作业': 'primary',
        '考试': 'success',
        '练习': 'warning',
        '资料学习': 'info'
      }
      return typeMap[type] || 'info'
    }
  }
}
</script>

<style scoped>
.student-report-container {
  padding: 20px;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.report-header h3 {
  margin: 0;
  color: #409EFF;
  font-size: 18px;
}

.report-actions {
  display: flex;
  gap: 10px;
}

.overview-card {
  margin-bottom: 20px;
}

.overview-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.overview-icon {
  font-size: 32px;
  color: #409EFF;
  margin-right: 15px;
}

.overview-content {
  flex: 1;
}

.overview-number {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.overview-label {
  font-size: 12px;
  color: #606266;
}

.progress-row {
  margin-bottom: 20px;
}

.progress-content {
  padding: 10px 0;
}

.progress-item {
  margin-bottom: 20px;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.progress-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.progress-text {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.score-display {
  text-align: center;
}

.score-number {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.score-range {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}

.knowledge-card,
.track-card,
.suggestions-card,
.achievements-card,
.improvements-card {
  margin-bottom: 20px;
}

.knowledge-content {
  padding: 10px 0;
}

.knowledge-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.knowledge-item:last-child {
  margin-bottom: 0;
}

.knowledge-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.knowledge-name {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.knowledge-progress {
  margin-bottom: 10px;
}

.knowledge-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
  margin-top: 5px;
}

.track-content {
  padding: 10px 0;
}

.track-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.track-item:last-child {
  border-bottom: none;
}

.track-date {
  font-size: 14px;
  color: #409EFF;
  font-weight: bold;
  margin-right: 15px;
  min-width: 100px;
}

.track-info {
  flex: 1;
}

.track-content-text {
  font-size: 14px;
  color: #303133;
  margin-bottom: 5px;
}

.track-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.track-time {
  font-size: 12px;
  color: #909399;
}

.suggestions-list,
.achievements-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestions-list li,
.achievements-list li {
  padding: 8px 0;
  color: #606266;
  line-height: 1.6;
}

.suggestions-list li i {
  color: #409EFF;
  margin-right: 8px;
}

.achievements-list li i {
  color: #E6A23C;
  margin-right: 8px;
}

.improvements-content {
  padding: 10px 0;
}

.empty-card {
  text-align: center;
  padding: 40px;
}

.empty-content {
  padding: 40px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-content h3 {
  color: #606266;
  margin-bottom: 10px;
}

.empty-content p {
  color: #909399;
  margin-bottom: 30px;
}
</style>
